import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUsers } from 'hooks/useUsers';
import { getDancers, getJudges, getStatuses, getDancerClasses, getCountries } from '@redux/features/ephanSlice';
import { getOrganizations } from '@redux/features/organizationsSlice';
import { formConfig } from 'pages/Users/formConfig';
import { activeUsersTypes } from 'constants/usersTypes';
import { TabSelector } from 'components/Button/TabSelector';
import { Container } from 'components/Container';
import { NoResults } from 'components/NoResults';
import { Dropdown } from 'components/Dropdown';
import { Button } from 'components/Button';
import { Loader } from 'components/Loader';
import { Input } from 'components/Input';
import { Seo } from 'components/Seo';
import { UserCard } from 'pages/Users/UserCard';
import styles from 'pages/Users/Users.scss';

export const Users = () => {
  const { dancers, dancersMeta, judges, judgesMeta, dancerClasses, statuses, countries, isLoading, isFetchingMore, error } =
    useSelector((state) => state.ephan);
  const { organizations } = useSelector((state) => state.organizations);
  const [usersList, setUsersList] = useState([]);
  const dispatch = useDispatch();

  const [activeTabName, setActiveTabName] = useState(activeUsersTypes[0]);
  const currentMeta = activeTabName === 'judges' ? judgesMeta : dancersMeta;

  // Dancers/judges load page by page, so deriving the filter from whatever is
  // loaded so far only ever shows the countries seen up to that point. Member
  // federations are a fixed, complete list — the same one the Members page
  // uses — so it works as a stable set of options regardless of pagination.
  const allCountries = useMemo(
    () => Array.from(new Set(organizations.map((organization) => organization.country).filter(Boolean))).sort(),
    [organizations]
  );

  const dynamicFormConfig = useMemo(
    () =>
      formConfig.map((item) => {
        if (item.name === 'country') {
          return {
            ...item,
            options: allCountries,
            printable: true,
            zIndex: 15,
          };
        }
        return item;
      }),
    [allCountries]
  );

  const fetchUsersPage = useCallback(
    (params) => {
      if (activeTabName === 'dancers') {
        dispatch(getDancers(params));
      } else if (activeTabName === 'judges') {
        dispatch(getJudges(params));
      }
    },
    [activeTabName, dispatch]
  );

  const {
    users,
    hasMore,
    loadMore,
    formState,
    activeTypeIndex,
    setActiveTypeIndex,
    handleFilterChange,
    handleSubmit,
    clearFilters,
  } = useUsers(usersList, statuses, activeUsersTypes, currentMeta, fetchUsersPage);

  useEffect(() => {
    const tabName = activeUsersTypes[activeTypeIndex] || activeUsersTypes[0];
    setActiveTabName(tabName);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTypeIndex]);

  const observerTarget = useRef(null);

  useEffect(() => {
    dispatch(getStatuses());
    dispatch(getCountries());
    if (!organizations.length) {
      dispatch(getOrganizations());
    }
    if (activeTabName === 'dancers' && !dancerClasses) {
      dispatch(getDancerClasses());
    }
  }, [dispatch, activeTabName, dancerClasses, organizations.length]);

  useEffect(() => {
    setUsersList(activeTabName === 'judges' ? judges : dancers);
  }, [activeTabName, judges, dancers]);

  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '300px' }
    );

    observer.observe(target);

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasMore, isFetchingMore, isLoading, loadMore, users.length]);

  return (
    <Container>
      <Seo />
      <div className={styles.page}>
        <h1 className={styles.title}>List of active users</h1>
        <div className={styles.pageContent}>
          <section className={styles.navigation}>
            <TabSelector
              tabs={activeUsersTypes}
              activeTabIndex={activeTypeIndex}
              setActiveTabIndex={setActiveTypeIndex}
            />
            <form className={styles.searchForm} onSubmit={handleSubmit}>
              {dynamicFormConfig.map(({ name, placeholder, options, zIndex, printable }) =>
                options ? (
                  <Dropdown
                    className={styles.dropdownFilter}
                    options={options}
                    selectedValue={formState[name]}
                    placeholder={placeholder}
                    onChange={(option) => handleFilterChange(name, option)}
                    printable={printable}
                    zIndex={zIndex}
                    key={name}
                  />
                ) : (
                  <Input
                    wrapperClassName={styles.input}
                    key={name}
                    name={name}
                    inputValue={formState[name]}
                    placeholder={placeholder}
                    onChange={(e) => handleFilterChange(name, e.target.value)}
                  />
                )
              )}
              <Button className={styles.searchBtn} text='Search' type='submit' searchStyle />
              <Button
                className={styles.clearFilters}
                text='Clear filters'
                type='reset'
                onClick={clearFilters}
                ghostStyle
              />
            </form>
          </section>
          <div className={styles.contentArea}>
            {error && <p className={styles.error}>{error}</p>}
            {isLoading ? (
              <Loader />
            ) : users.length ? (
              <>
                <ul className={styles.users}>
                  {users.map((user) => (
                    <UserCard
                      role={activeTabName}
                      user={user}
                      key={user.id}
                      countries={countries}
                      dancerClasses={dancerClasses}
                    />
                  ))}
                </ul>
                <div ref={observerTarget} className={styles.sentinel}>
                  {(isFetchingMore || hasMore) && <Loader />}
                </div>
              </>
            ) : (
              <NoResults />
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};
